import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { to12h, utilBadgeStyle, seqColor } from '../utils/classroomUtilizationHelpers';

function TimeSlotChartTooltip({ active, payload, occupiedLabel }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs shadow-lg">
      <p className="font-semibold text-gray-800">{d.label}</p>
      <p className="text-gray-600">
        Occupancy: <span className="font-semibold">{d.occupancyPct}%</span>
      </p>
      <p className="text-gray-400">{d.occupiedCount} of {d.maxPossible} {occupiedLabel} busy</p>
    </div>
  );
}

// Shared "Time Slot Analysis" chart + table, used both for the aggregate
// (all-rooms) report and the single-room calculation-detail view. Extra
// room-specific insight (free ranges, average busy window) is opt-in via
// props so the aggregate view doesn't need to carry them.
export default function TimeSlotAnalysisChart({
  analysis,
  description,
  occupiedLabel = 'days',
  freeRanges,
  averageBusyWindow,
}) {
  if (!analysis || !analysis.slots || analysis.slots.length === 0) return null;

  const chartData = analysis.slots.map((s) => ({
    label: to12h(s.startTime),
    occupancyPct: s.occupancyPct ?? 0,
    occupiedCount: s.occupiedRoomDays ?? s.occupiedDays,
    maxPossible: s.maxPossible ?? s.dayCount,
  }));

  return (
    <div className="mt-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <h4 className="mb-1 text-sm font-bold text-gray-900">Time Slot Analysis</h4>
      <p className="mb-4 text-xs text-gray-500">{description}</p>

      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
          <div className="text-lg font-bold text-red-700">
            {analysis.peakSlot ? `${to12h(analysis.peakSlot.startTime)} – ${to12h(analysis.peakSlot.endTime)}` : '—'}
          </div>
          <div className="text-[11px] text-gray-500">
            Busiest hour ({analysis.peakSlot?.occupancyPct ?? '—'}% occupancy)
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
          <div className="text-lg font-bold text-emerald-700">
            {analysis.quietSlot ? `${to12h(analysis.quietSlot.startTime)} – ${to12h(analysis.quietSlot.endTime)}` : '—'}
          </div>
          <div className="text-[11px] text-gray-500">
            Quietest hour ({analysis.quietSlot?.occupancyPct ?? '—'}% occupancy)
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-center">
          <div className="text-lg font-bold text-gray-900">{analysis.averageOccupancyPct ?? '—'}%</div>
          <div className="text-[11px] text-gray-500">Average occupancy across all hours</div>
        </div>
      </div>

      {(averageBusyWindow || (freeRanges && freeRanges.length > 0)) && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {averageBusyWindow && (
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-center">
              <div className="text-base font-bold text-indigo-800">
                {to12h(averageBusyWindow.startTime)} – {to12h(averageBusyWindow.endTime)}
              </div>
              <div className="text-[11px] text-indigo-700">
                Average utilized time range (on days this room is used)
              </div>
            </div>
          )}
          {freeRanges && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
              {freeRanges.length > 0 ? (
                <>
                  <div className="text-sm font-bold text-emerald-800">
                    {freeRanges.map((r) => `${to12h(r.startTime)}–${to12h(r.endTime)}`).join(', ')}
                  </div>
                  <div className="text-[11px] text-emerald-700">Free ranges (&le;20% occupancy — good for new classes)</div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-emerald-800">None</div>
                  <div className="text-[11px] text-emerald-700">No consistently free ranges found</div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e1e0d9" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#898781' }} interval={0} angle={-45} textAnchor="end" height={50} />
            <YAxis
              tick={{ fontSize: 10, fill: '#898781' }}
              domain={[0, 100]}
              tickFormatter={(v) => `${v}%`}
              width={40}
            />
            <Tooltip content={<TimeSlotChartTooltip occupiedLabel={occupiedLabel} />} cursor={{ fill: 'rgba(42,120,214,0.08)' }} />
            <Bar dataKey="occupancyPct" radius={[4, 4, 0, 0]} maxBarSize={28}>
              {chartData.map((entry, idx) => (
                <Cell key={idx} fill={seqColor(entry.occupancyPct)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full min-w-[520px] text-left text-xs">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 uppercase tracking-wide text-gray-400">
              <th className="px-3 py-2">Time Slot</th>
              <th className="px-3 py-2 text-center capitalize">{occupiedLabel} Occupied</th>
              <th className="px-3 py-2 text-center">Max Possible</th>
              <th className="px-3 py-2 text-center">Occupancy %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {analysis.slots.map((s) => (
              <tr key={s.startTime} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-gray-700">{to12h(s.startTime)} – {to12h(s.endTime)}</td>
                <td className="px-3 py-2 text-center text-gray-600">{s.occupiedRoomDays ?? s.occupiedDays}</td>
                <td className="px-3 py-2 text-center text-gray-600">{s.maxPossible ?? s.dayCount}</td>
                <td className="px-3 py-2 text-center">
                  <span className="inline-block rounded-full px-2 py-1 font-bold" style={utilBadgeStyle(s.occupancyPct)}>
                    {s.occupancyPct === null ? '—' : `${s.occupancyPct}%`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
